import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, XCircle, PackageOpen, Home, Settings2, Pencil, EyeOff, LineChart, Upload, PauseCircle, PlayCircle } from 'lucide-react';
import LoadingSpinner from '../components/common/LoadingSpinner';
import StandardLayout from '../components/common/StandardLayout';
import PageHeader from '../components/common/PageHeader';
import Button from '../components/ui/Button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card';
import EditListingDialog from '../components/marketplace/EditListingDialog';
import PauseStrategiesDialog from '../components/marketplace/PauseStrategiesDialog';
import UnpublishBasketDialog from '../components/marketplace/UnpublishBasketDialog';
import UploadBacktestDialog from '../components/marketplace/UploadBacktestDialog';
import { MarketplaceListing } from '../types/marketplace';
import marketplaceService from '../services/marketplaceService';
import { useAuth } from '../context/AuthContext';
import { CHIP_METADATA, CHIP_PILL, CHIP_RISK } from '../utils/chipColors';

const AdminMarketplacePage: React.FC = () => {
  const { isAdmin } = useAuth();
  const adminAccess = isAdmin;
  const [listings, setListings] = useState<MarketplaceListing[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingListing, setEditingListing] = useState<MarketplaceListing | null>(null);
  const [unpublishingListing, setUnpublishingListing] = useState<MarketplaceListing | null>(null);
  const [republishingId, setRepublishingId] = useState<string | null>(null);
  const [backtestListing, setBacktestListing] = useState<MarketplaceListing | null>(null);
  // Pause/resume action state: the listing whose dialog is currently open,
  // plus the latest toast message rendered above the table.
  const [pauseDialog, setPauseDialog] = useState<{ listing: MarketplaceListing; mode: 'pause' | 'resume' } | null>(null);
  const [pauseToast, setPauseToast] = useState<{ basketId: string; message: string; tone: 'success' | 'error' } | null>(null);

  const fetchListings = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await marketplaceService.getAdminListings();
      setListings(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load listings');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchListings();
  }, [fetchListings]);

  // Auto-dismiss pause toast after 6s. Long enough to read the count, short
  // enough that a re-pause attempt doesn't fight a stale stale banner.
  useEffect(() => {
    if (!pauseToast) return;
    const t = setTimeout(() => setPauseToast(null), 6000);
    return () => clearTimeout(t);
  }, [pauseToast]);

  const handleEditSuccess = (updated: MarketplaceListing) => {
    setListings((prev) =>
      prev.map((l) => (l.basket_id === updated.basket_id ? updated : l))
    );
    setEditingListing(null);
  };

  const handleBacktestUpdate = (updated: MarketplaceListing) => {
    setListings((prev) =>
      prev.map((l) => (l.basket_id === updated.basket_id ? updated : l))
    );
    setBacktestListing(null);
  };

  const handleUnpublishSuccess = () => {
    setUnpublishingListing(null);
    // Re-fetch to get the canonical updated listing row (the unpublish endpoint
    // returns a status message, not the full listing).
    fetchListings();
  };

  // Re-publish flips `is_published=true` via update_listing. The backend
  // re-runs `assert_can_publish` on every existing group (R1 guard) so a
  // publisher who lost membership since the original publish can't resurrect
  // the listing into a group they no longer have access to.
  const handleRepublish = async (listing: MarketplaceListing) => {
    if (republishingId) return;
    setRepublishingId(listing.basket_id);
    setError(null);
    try {
      const updated = await marketplaceService.updateListing(listing.basket_id, {
        is_published: true,
      });
      setListings((prev) =>
        prev.map((l) => (l.basket_id === updated.basket_id ? updated : l))
      );
    } catch (err: any) {
      setError(err.message || 'Failed to re-publish basket');
    } finally {
      setRepublishingId(null);
    }
  };

  // Pause/resume open the custom PauseStrategiesDialog (matches the 2025
  // minimalist design used by EditListingDialog / UnpublishBasketDialog —
  // no native window.confirm). The dialog calls back with the action result.
  // We re-fetch listings so the active/total counts refresh and the row
  // button flips between Pause and Resume without a manual reload.
  const handlePauseDialogSuccess = (result: { updated_count: number; total: number }) => {
    const verb = pauseDialog?.mode === 'resume' ? 'Resumed' : 'Paused';
    const tail = pauseDialog?.mode === 'resume'
      ? 'Entries resume firing on the next scheduled tick.'
      : 'Existing positions will exit naturally.';
    setPauseToast({
      basketId: pauseDialog?.listing.basket_id ?? '',
      message: `${verb} ${result.updated_count} of ${result.total} strategies. ${tail}`,
      tone: 'success',
    });
    setPauseDialog(null);
    fetchListings();
  };

  const publishedCount = listings.filter((l) => l.is_published).length;
  const unpublishedCount = listings.filter((l) => !l.is_published).length;

  // Compact INR formatter: 500000 → "₹5L", 1500000 → "₹15L", 12500 → "₹12.5K".
  // Falls back to "—" for nullish / NaN / non-finite values (F4 guard against
  // a malformed payload producing "₹InfinityCr").
  const formatCapital = (v: number | undefined): string => {
    if (v == null || !Number.isFinite(v)) return '—';
    if (v >= 10_000_000) return `₹${(v / 10_000_000).toFixed(v % 10_000_000 ? 1 : 0)}Cr`;
    if (v >= 100_000) return `₹${(v / 100_000).toFixed(v % 100_000 ? 1 : 0)}L`;
    if (v >= 1_000) return `₹${(v / 1_000).toFixed(v % 1_000 ? 1 : 0)}K`;
    return `₹${v}`;
  };

  return (
    <StandardLayout>
      <PageHeader
        title="Manage Listings"
        pageType="management"
        icon={<Settings2 className="w-5 h-5" />}
        breadcrumbs={[
          { label: 'Home', href: '/', icon: <Home className="w-4 h-4" /> },
          { label: 'Platform' },
          { label: 'Listings' },
        ]}
        actions={
          <Button
            variant="outline"
            onClick={fetchListings}
            leftIcon={<RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />}
            className="w-full sm:w-auto"
            disabled={isLoading}
          >
            Refresh
          </Button>
        }
      />

      <div className="space-y-4">
        {/* Admin guard */}
        {!adminAccess && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 text-center">
            <XCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
            <p className="text-red-800 dark:text-red-200 font-medium">Access Denied</p>
            <p className="text-red-600 dark:text-red-400 text-sm mt-1">This page is only accessible to administrators.</p>
          </div>
        )}

        {adminAccess && (
          <>
            {/* Stats */}
            {!isLoading && listings.length > 0 && (
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                  <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Total</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white mt-1">{listings.length}</p>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                  <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Published</p>
                  <p className="text-xl font-bold text-blue-600 dark:text-blue-400 mt-1">{publishedCount}</p>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                  <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Unpublished</p>
                  <p className="text-xl font-bold text-gray-400 mt-1">{unpublishedCount}</p>
                </div>
              </div>
            )}

            {/* Loading */}
            {isLoading && <LoadingSpinner message="Loading listings..." />}

            {/* Error */}
            {error && !isLoading && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <div className="flex items-center gap-2">
                  <XCircle className="h-5 w-5 text-red-600" />
                  <span className="text-red-800 dark:text-red-200 text-sm">{error}</span>
                </div>
              </div>
            )}

            {/* Pause action toast */}
            {pauseToast && (
              <div className={`rounded-lg p-3 text-sm ${
                pauseToast.tone === 'success'
                  ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-200'
                  : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200'
              }`}>
                {pauseToast.message}
              </div>
            )}

            {/* Empty */}
            {!isLoading && !error && listings.length === 0 && (
              <Card>
                <CardContent>
                  <div className="text-center py-12">
                    <PackageOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No Listings Yet</h3>
                    <p className="text-gray-600 dark:text-gray-400">Publish a basket from the Baskets page to get started.</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Table */}
            {!isLoading && !error && listings.length > 0 && (
              <Card className="overflow-hidden">
                <CardHeader>
                  <CardTitle>Listings ({listings.length})</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-700 border-y border-gray-200 dark:border-gray-700">
                        <tr>
                          {['Title', 'Category', 'Risk Level', 'Capital', 'Lots', 'Groups', 'Subscribers', 'Status', 'Backtest', 'Updated', 'Actions'].map((h) => (
                            <th
                              key={h}
                              className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {listings.map((listing) => (
                          <tr key={listing.basket_id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div>
                                <p className="text-sm font-medium text-gray-900 dark:text-white">{listing.title}</p>
                                {listing.strategies_count !== undefined && (
                                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                                    {listing.strategies_count} strategies
                                  </p>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                              {listing.category}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {/* Risk-level chip routed through canonical CHIP_RISK so the admin
                                  listings table matches MarketplaceBasketCard / SubscriptionCard
                                  (bg-100, not the pre-Phase-0 lighter bg-50). */}
                              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                                listing.risk_level === 'LOW'
                                  ? CHIP_RISK.LOW
                                  : listing.risk_level === 'HIGH'
                                  ? CHIP_RISK.HIGH
                                  : CHIP_RISK.MEDIUM
                              }`}>
                                {listing.risk_level}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300 tabular-nums">
                              {formatCapital(listing.capital_required)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300 tabular-nums">
                              {listing.min_lot_multiplier ?? 1}-{listing.max_lot_multiplier ?? 1}x
                              {listing.base_lot_size && listing.base_lot_size !== 1 && (
                                <span className="text-xs text-gray-400 dark:text-gray-500 ml-1">
                                  · base {listing.base_lot_size}
                                </span>
                              )}
                            </td>
                            <td className="px-6 py-4">
                              {listing.published_to_groups && listing.published_to_groups.length > 0 ? (
                                <div className="flex flex-wrap gap-1 max-w-[200px]">
                                  {[...listing.published_to_groups].sort().map((g) => (
                                    <span
                                      key={g}
                                      title={g}
                                      // Publisher-group tag — `public` uses
                                      // CHIP_PILL.ACCENT for highlighted state
                                      // (adds a 1px blue ring; pre-Phase-0 had
                                      // no ring — small visual addition).
                                      // Other groups use CHIP_METADATA.DEFAULT
                                      // (text-gray-600; pre-Phase-0 was
                                      // text-gray-700 — one shade lighter).
                                      className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
                                        g === 'public' ? CHIP_PILL.ACCENT : CHIP_METADATA.DEFAULT
                                      }`}
                                    >
                                      {g}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-xs text-gray-400 dark:text-gray-500">—</span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                              {listing.subscriber_count}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {/* Published / Unpublished pair routed through canonical chipColors.
                                  Published → CHIP_PILL.ACCENT (adds a 1px blue ring vs pre-Phase-0
                                  no-ring; small visual addition consistent with other Family B pills).
                                  Unpublished → CHIP_METADATA.DEFAULT (text-500 → text-600 light;
                                  text-400 → text-300 dark — slightly darker for readability). */}
                              {listing.is_published ? (
                                <span className={`inline-flex items-center text-xs font-semibold px-2 py-0.5 rounded-full ${CHIP_PILL.ACCENT}`}>
                                  Published
                                </span>
                              ) : (
                                <span className={`inline-flex items-center text-xs font-semibold px-2 py-0.5 rounded-full ${CHIP_METADATA.DEFAULT}`}>
                                  Unpublished
                                </span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {listing.backtest_uploaded_at ? (
                                <span className="inline-flex items-center gap-1 text-xs text-gray-600 dark:text-gray-300">
                                  <LineChart className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
                                  {listing.backtest_row_count} rows
                                </span>
                              ) : (
                                <span className="text-xs text-gray-400 dark:text-gray-500">—</span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {listing.updated_at
                                ? new Date(listing.updated_at).toLocaleDateString()
                                : '—'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => setEditingListing(listing)}
                                  className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-md bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-900/50 transition-colors"
                                >
                                  <Pencil className="h-3.5 w-3.5" />
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setBacktestListing(listing)}
                                  className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-md bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-300 dark:hover:bg-emerald-900/50 transition-colors"
                                >
                                  <Upload className="h-3.5 w-3.5" />
                                  {listing.backtest_uploaded_at ? 'Update Backtest' : 'Upload Backtest'}
                                </button>
                                {listing.is_published && (() => {
                                  // Flip the button when every strategy is paused. We need both counts
                                  // (active + total) to distinguish "no strategies at all" (hide the
                                  // button) from "all strategies paused" (show Resume).
                                  const total = listing.strategies_total_count ?? listing.strategies_count ?? 0;
                                  const active = listing.strategies_active_count ?? listing.strategies_count ?? total;
                                  if (total === 0) return null;
                                  const allPaused = active === 0;
                                  return allPaused ? (
                                    <button
                                      type="button"
                                      onClick={() => setPauseDialog({ listing, mode: 'resume' })}
                                      title="Resume firing entries for everyone (you + subscribers)."
                                      className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-md bg-green-50 text-green-700 hover:bg-green-100 dark:bg-green-900/30 dark:text-green-300 dark:hover:bg-green-900/50 transition-colors"
                                    >
                                      <PlayCircle className="h-3.5 w-3.5" />
                                      Resume Strategies
                                    </button>
                                  ) : (
                                    <button
                                      type="button"
                                      onClick={() => setPauseDialog({ listing, mode: 'pause' })}
                                      title="Stop new entries for everyone (you + subscribers). Existing positions exit naturally."
                                      className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-md bg-indigo-50 text-indigo-700 hover:bg-indigo-100 dark:bg-indigo-900/30 dark:text-indigo-300 dark:hover:bg-indigo-900/50 transition-colors"
                                    >
                                      <PauseCircle className="h-3.5 w-3.5" />
                                      Pause Strategies
                                    </button>
                                  );
                                })()}
                                {listing.is_published ? (
                                  <button
                                    type="button"
                                    onClick={() => setUnpublishingListing(listing)}
                                    title="Stop new subscriptions. Existing subscribers keep running."
                                    className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-md bg-amber-50 text-amber-700 hover:bg-amber-100 dark:bg-amber-900/30 dark:text-amber-300 dark:hover:bg-amber-900/50 transition-colors"
                                  >
                                    <EyeOff className="h-3.5 w-3.5" />
                                    Unpublish
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => handleRepublish(listing)}
                                    disabled={republishingId === listing.basket_id}
                                    title="Re-list this basket on the marketplace."
                                    className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-md bg-green-50 text-green-700 hover:bg-green-100 dark:bg-green-900/30 dark:text-green-300 dark:hover:bg-green-900/50 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                                  >
                                    <Upload className="h-3.5 w-3.5" />
                                    {republishingId === listing.basket_id ? 'Re-publishing…' : 'Re-publish'}
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>

      {/* Edit Dialog */}
      {editingListing && (
        <EditListingDialog
          listing={editingListing}
          onClose={() => setEditingListing(null)}
          onSuccess={handleEditSuccess}
        />
      )}

      {/* Backtest Upload Dialog */}
      {backtestListing && (
        <UploadBacktestDialog
          listing={backtestListing}
          onClose={() => setBacktestListing(null)}
          onSuccess={handleBacktestUpdate}
        />
      )}

      {/* Unpublish confirmation */}
      {unpublishingListing && (
        <UnpublishBasketDialog
          listing={unpublishingListing}
          onClose={() => setUnpublishingListing(null)}
          onSuccess={handleUnpublishSuccess}
        />
      )}

      {/* Pause / resume strategies confirmation */}
      {pauseDialog && (
        <PauseStrategiesDialog
          listing={pauseDialog.listing}
          mode={pauseDialog.mode}
          onClose={() => setPauseDialog(null)}
          onSuccess={handlePauseDialogSuccess}
        />
      )}
    </StandardLayout>
  );
};

export default AdminMarketplacePage;
